// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, func, object } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Filter, ArrowDownWideNarrow, X } from 'lucide-react';
import {
  TextInput,
  Button,
  RadioButtonGroup,
  RadioButton,
  DatePicker,
  DatePickerInput,
} from '@carbon/react';
import ReactSelect from 'react-select';

const StyledFilterSerialNumbers = styled.div`
  .mobileToolbar {
    display: none;
    gap: 1em;
    margin-bottom: 18px;

    &.fixed {
      display: none;
      justify-content: center;
      align-items: center;
      padding: 15px;
      margin: 0;
      width: 100vw;
      height: 80px;
      position: fixed;
      bottom: 0;
      left: 0;
      transition: transform 0.2s ease;
      overflow: visible;
      opacity: ${ props => props.isVisible ? 1 : 0 };
      visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
      background-color: ${ props => props.theme.colors.white };
      box-shadow: 0 3px 6px #00000029;
      z-index: 8999;
    }

    button {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 8px;
      background: #E7ECF2;
      border: 1px solid #B2C2D5;
      border-radius: 10px;
      cursor: pointer;

      .title {
        display: flex;
        align-items: center;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;

        svg {
          margin-right: 8px;
        }
      }

      .info {
        color: #707070;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }

  .backDrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    background: #000000b3;
    display: ${ props => props.mobileSortOpen ? 'flex' : 'none' };
    align-items: flex-end;

    .content {
      flex: 1;
      padding: 15px;
      background: #fff;

      .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;

        h3 {
          font-size: 20px;
          line-height: 27px;
        }

        button {
          background: transparent;
          border: none;
          cursor: pointer;
        }
      }

      .cds--radio-button-group {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .cds--radio-button-wrapper {
          margin: 7px 0;
        }
      }
    }
  }

  .filterSection {
    display: flex;
    flex-direction: column;
    padding: 25px 0;
    gap: 20px;

    .mobileHeader {
      display: none;

      .mobileTitleSection {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 25px 15px 15px;

        h2 {
          font-size: 20px;
        }

        button {
          cursor: pointer;
          color: #D52B1E;
          background: transparent;
          border: none;
        }
      }

      .activeFilters {
        display: flex;
        background: #F3F2F2;
        padding: 15px;
        flex-direction: column;

        > span {
          font-weight: 600;
        }

        .activeFilterList {
          padding: 10px 0;
          display: flex;
          text-wrap: nowrap;
          overflow-x: scroll;

          .filter { 
            margin-right: 10px;
          }
        }

        button {
          cursor: pointer;
          background: transparent;
          border: none;

          svg {
            stroke: #D52B1E;
          }
        }
      }
    }

    .filterContainer {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .filters {
        display: flex;
        align-items: center;
        gap: 10px;

        > div:first-of-type {
          margin-bottom: 0;
        }

        > div:nth-of-type(3) {
          margin: 0;
        }

        > div .cds--combo-box {
          margin: 0;
          border: none;
        }

        .dateRange {
          display: flex;

          > div {
            padding: 0 5px;
            flex: 0 1 auto;
            min-width: 50%;
          }

          .cds--date-picker.cds--date-picker--single .cds--date-picker__input {
            width: 100%;
          }
        }

        input[type="text"] {
          min-width: 260px;

          &#user-search {
            min-width: 360px;
          }
        }
      }

      .actionsSection {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;

        .multiSelect {
          flex: 1;
          max-width: 50%;
        }
  
        .actions {
          display: flex;
          gap: 10px;
  
          button.cds--btn {
            height: 40px;
            max-width: none;
            min-width: 170px;
            -webkit-box-pack: center;
            justify-content: center;
            font-size: 1rem;
            text-align: center;
  
            &.deleteFilters {
              border: 1px solid #D52B1E;
              color: #D52B1E;
  
              &:hover {
                background: transparent;
              }
            }
          }
        }
      }
    }


    .cds--label {
      display: none;
    }

    .cds--select-input, .cds--date-picker__input, .cds--text-input {
      background: #fff;
      border: 1px solid #e2e2e2;
      border-radius: 5px;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .filterSection {
      display: ${ props => props.mobileFiltersOpen ? 'flex' : 'none' };
      position: fixed;
      inset: 0;
      background: #fff;
      z-index: 9001;
      margin-top: 0;
      overflow-y: scroll;

      .cds--label {
        display: inline-block;
        font-size: 15px;
        font-weight: 600;
        color: #3a3a3a;
      }

      .mobileHeader {
        display: block;
      }

      .filterContainer {
        .filters {
          flex-direction: column;
          padding: 15px;
          width: 100%;

          .cds--form-item {
            width: 100%;
          }

          .dateRange {
            flex-direction: column;
            width: 100%;

            .cds--date-picker {
              padding: 10px 0;
            }

            .cds--date-picker,
            .cds--date-picker-container,
            .cds--date-picker-input__wrapper span {
              width: 100%;
            }
          }

          input[type="text"] {
            &#user-search {
              min-width: auto;
            }
          }
        }

        .actionsSection {
          gap: 20px;
          flex-direction: column;
          padding: 15px;

          .multiSelect {
            min-width: 100%;
          }
  
          .actions {
            width: 100%;
  
            button.cds--btn {
              min-width: 100%;
  
              &.deleteFilters {
                display: none;
              }
            }
          }
        }
      }

    }

    .mobileToolbar {
      display: flex;

      &.fixed {
        display: flex;
      }
    }
  }
`;

const FilterSerialNumbers = ({
  manufacturers,
  register,
  setValue,
  onSubmit,
  onClearFilters,
  onClearFilter,
  activeFilters,
  setMobileFiltersOpen,
  mobileFiltersOpen,
  setMobileSortOpen,
  mobileSortOpen,
  sortOptions,
  currentSort,
  handleMobileOrderChange,
  dateStart,
  dateEnd,
  onChangeDate,
  selectedManufacturers,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const filterKeys = useMemo(() => [
    { key: 'serial_number', label: t('pages.serialNumber') },
    { key: 'product_code', label: t('pages.productCode') },
    { key: 'date_start', label: t('orders.dateStart') },
    { key: 'date_end', label: t('orders.dateEnd') },
    { key: 'brand[]', label: t('pages.manufacturerName') },
  ], [t]);

  const renderFilter = ({ key, value }) => {
    const filterName = filterKeys.find(item => item.key === key)?.label;
    let filterValue = value;

    if (key === 'brand[]') {
      const ids = Array.isArray(value) ? value : [value];
      const manufacturerNames = ids.map(id => {
        const manufacturer = manufacturers.find(m => m.value === id);
        return manufacturer?.label;
      }).filter(Boolean).join(', ');
      filterValue = manufacturerNames || value;
    }

    return (
      <div>
        <span>{ `${ filterName }: ${ filterValue }` }</span>
        <button type="button" onClick={ () => onClearFilter(key) }><X /></button>
      </div>
    );
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    ...register(fieldName),
  });

  return (
    <StyledFilterSerialNumbers theme={ theme } mobileSortOpen={ mobileSortOpen } mobileFiltersOpen={ mobileFiltersOpen }>
      <div className="mobileToolbar">
        <button type="button" onClick={ () => setMobileFiltersOpen(true) }>
          <span className="title">
            <Filter />
            { t('filters.filter') }
          </span>
          <span className="info">{ `${ Object.keys(activeFilters).length } ${t('filters.selectedFilters')}`} </span>
        </button>
        <button type="button" onClick={ () => setMobileSortOpen(true) }>
          <span className="title">
            <ArrowDownWideNarrow />
            { t('products.sort') }
          </span>
          <span className="info">{ currentSort?.label }</span>
        </button>
      </div>
      <div className="mobileSort">
        <div className="backDrop">
          <div className="content">
            <div className="header">
              <h3>{ t('products.sortBy') }</h3>
              <button type="button" onClick={ () => setMobileSortOpen(false) }>
                <X />
              </button>
            </div>
            <RadioButtonGroup
              name="sortSerialNumbers"
              valueSelected={ currentSort?.id }
              onChange={ handleMobileOrderChange }
            >
              { sortOptions.map(opt => (
                <RadioButton key={ opt.id } labelText={ opt.label } value={ opt.id } id={ opt.id } />
              )) }
            </RadioButtonGroup>
          </div>
        </div>
      </div>
      <div className="filterSection">
        <div className="mobileHeader">
          <div className="mobileTitleSection">
            <h2>{ t('orders.filters') }</h2>
            <button
              type="button"
              onClick={ () => {
                onClearFilters();
                setMobileFiltersOpen(false);
              } }
            >
              { t('products.clearAllFilters')}
            </button>
          </div>
          { Object.keys(activeFilters).length > 0 && (
            <div className="activeFilters">
              <span>{ `${ Object.keys(activeFilters).length } ${ t('orders.activeFilters') }` }</span>
              <div className="activeFilterList">
                { Object.entries(activeFilters).map(([key, value]) => <div key={ key } className="filter">{ renderFilter({ key, value }) }</div>) }
              </div>
            </div>
          )}
        </div>
        <div className="filterContainer">
          <div className="filters">
            <TextInput
              id="serial_number"
              labelText={ t('pages.serialNumber') }
              placeholder={ t('pages.serialNumber') }
              size="md"
              { ...sharedProps('serial_number') }
            />
            <TextInput
              id="product_code"
              labelText={ t('pages.productCode') }
              placeholder={ t('pages.productCode') }
              size="md"
              { ...sharedProps('product_code') }
            />
            <div className="dateRange">
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ dateStart }
                maxDate={ dateEnd }
                onChange={ date => onChangeDate('date_start', date) }
              >
                <DatePickerInput
                  placeholder={ t('orders.dateStart') }
                  labelText={ t('orders.dateStart') }
                  id="date-start"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ dateEnd }
                minDate={ dateStart }
                onChange={ date => onChangeDate('date_end', date) }
              >
                <DatePickerInput
                  placeholder={ t('orders.dateEnd') }
                  labelText={ t('orders.dateEnd') }
                  id="date-end"
                  size="md"
                />
              </DatePicker>
            </div>
          </div>
          <div className="actionsSection">
            <div className="multiSelect">
              <ReactSelect
                isMulti
                isLoading={ manufacturers.length === 0 }
                inputId="brands-select"
                options={ manufacturers }
                value={ selectedManufacturers }
                onChange={ items => setValue('manufacturer_name', items) }
                placeholder={ t('profile.paramsPlaceholder.manufacturer_id') }
                classNamePrefix="react-select"
                isSearchable
                closeMenuOnSelect={ false }
                getOptionValue={ option => option.value }
              />
            </div>
            <div className="actions">
              <Button
                className="filterOrders"
                kind="danger"
                onClick={ onSubmit }
              >
                { t('orders.filter') }
              </Button>
              { !!Object.keys(activeFilters).length && (
                <Button
                  className="deleteFilters"
                  kind="danger--ghost"
                  onClick={ onClearFilters }
                >
                  { t('order.deleteFilters') }
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </StyledFilterSerialNumbers>
  );
};

FilterSerialNumbers.propTypes = {
  manufacturers: array,
  register: func,
  setValue: func,
  onSubmit: func,
  onClearFilters: func,
  onClearFilter: func,
  activeFilters: object,
  setMobileFiltersOpen: func,
  mobileFiltersOpen: bool,
  setMobileSortOpen: func,
  mobileSortOpen: bool,
  sortOptions: array,
  currentSort: object,
  handleMobileOrderChange: func,
  dateStart: object,
  dateEnd: object,
  onChangeDate: func,
  selectedManufacturers: array,
};

FilterSerialNumbers.displayName = 'FilterSerialNumbersComponent';

export default FilterSerialNumbers;
